package co.maytes.standupbot.dashboard.components.feedback

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import co.maytes.standupbot.dashboard.components.motion.ExitGhost
import co.maytes.standupbot.dashboard.components.motion.Presence
import co.maytes.standupbot.dashboard.components.motion.presenceClassNames
import co.maytes.standupbot.dashboard.components.widgets.IconButton
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideX
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.ZIndex
import co.maytes.standupbot.shared.motion.PresenceState
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

private var modalCounter = 0

private const val SCRIM_CLASS = "sb-scrim"
private const val DIALOG_CLASS = "sb-dialog"

@Composable
fun Modal(
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    open: Boolean = true,
    title: String? = null,
    size: ModalSize = ModalSize.Sm,
    widthPx: Int? = null,
    fullscreen: Boolean = false,
    dismissOnScrim: Boolean = true,
    dismissOnEsc: Boolean = true,
    submitting: Boolean = false,
    showClose: Boolean = true,
    ariaLabel: String? = null,
    footer: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    Presence(visible = open, enterMs = Motion.MODAL_IN_MS, exitMs = Motion.MODAL_OUT_MS) { presence ->
        ModalLayer(
            presence = presence,
            onDismiss = onDismiss,
            modifier = modifier,
            title = title,
            size = size,
            widthPx = widthPx,
            fullscreen = fullscreen,
            dismissOnScrim = dismissOnScrim,
            dismissOnEsc = dismissOnEsc,
            submitting = submitting,
            showClose = showClose,
            ariaLabel = ariaLabel,
            footer = footer,
            content = content,
        )
    }
}

@Composable
private fun ModalLayer(
    presence: PresenceState,
    onDismiss: () -> Unit,
    modifier: Modifier,
    title: String?,
    size: ModalSize,
    widthPx: Int?,
    fullscreen: Boolean,
    dismissOnScrim: Boolean,
    dismissOnEsc: Boolean,
    submitting: Boolean,
    showClose: Boolean,
    ariaLabel: String?,
    footer: (@Composable () -> Unit)?,
    content: @Composable () -> Unit,
) {
    val instanceId = remember { modalCounter++ }
    val dialogId = remember(instanceId) { "sb-modal-$instanceId" }
    val titleId = remember(instanceId) { "sb-modal-title-$instanceId" }

    fun dismissIfAllowed() {
        if (!submitting) onDismiss()
    }

    val currentShown by rememberUpdatedState(presence.shown)
    val currentSubmitting by rememberUpdatedState(submitting)
    val currentDismissOnEsc by rememberUpdatedState(dismissOnEsc)
    val currentOnDismiss by rememberUpdatedState(onDismiss)

    DisposableEffect(dialogId) {
        val dialog = document.getElementById(dialogId) as? HTMLElement
        val previouslyFocused = document.activeElement as? HTMLElement

        if (dialog != null) {
            focusFirst(dialog)
        }
        val bodyStyle = document.body?.style
        val priorOverflow = bodyStyle?.getPropertyValue("overflow") ?: ""
        bodyStyle?.setProperty("overflow", "hidden")

        val listener: (Event) -> Unit = handler@{ e ->
            if (e !is KeyboardEvent || !currentShown) return@handler
            val activeDialog = document.getElementById(dialogId) as? HTMLElement ?: return@handler
            when {
                e.key == "Escape" -> {
                    if (currentDismissOnEsc && !currentSubmitting) {
                        e.preventDefault()
                        currentOnDismiss()
                    }
                }
                e.key == "Tab" -> trapTab(activeDialog, e)
            }
        }
        document.addEventListener("keydown", listener)

        onDispose {
            document.removeEventListener("keydown", listener)
            bodyStyle?.setProperty("overflow", priorOverflow)
            previouslyFocused?.focus()
        }
    }

    val showHeader = title != null || showClose

    Div(
        attrs = {
            classes(*presenceClassNames(SCRIM_CLASS, presence))
            ref { element ->
                onDispose {
                    if (element.classList.contains(Motion.OPEN_CLASS)) ExitGhost.play(element, Motion.MODAL_OUT_MS)
                }
            }
            style {
                property("position", "fixed")
                property("inset", "0")
                property("background-color", "rgba(9, 9, 11, 0.55)")
                property("backdrop-filter", "blur(4px)")
                property("z-index", "${ZIndex.modal}")
                property("display", "flex")
                property("align-items", if (fullscreen) "stretch" else "center")
                property("justify-content", "center")
                if (fullscreen) property("padding", "${Spacing.lg}")
                property("box-sizing", "border-box")
            }
            onClick { if (dismissOnScrim) dismissIfAllowed() }
        },
    ) {
        Div(
            attrs = modifier.toAttrs {
                classes(*presenceClassNames(DIALOG_CLASS, presence))
                attr("id", dialogId)
                attr("role", "dialog")
                attr("aria-modal", "true")
                attr("tabindex", "-1")
                if (title != null) {
                    attr("aria-labelledby", titleId)
                } else if (ariaLabel != null) {
                    attr("aria-label", ariaLabel)
                }
                style {
                    property("background-color", Palette.surface.cssRgb())
                    property("border-radius", "${Radius.lg}")
                    property("border", "1px solid ${Palette.border.cssRgb()}")
                    property("box-shadow", Shadow.LG)
                    property("box-sizing", "border-box")
                    if (fullscreen) {
                        property("width", "100%")
                        property("height", "100%")
                        property("max-width", "1400px")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("overflow", "hidden")
                    } else {
                        property("width", "${widthPx ?: size.widthPx}px")
                        property("max-width", "calc(100vw - 48px)")
                        property("max-height", "calc(100vh - 48px)")
                        property("overflow-y", "auto")
                        property("padding", "${Spacing.lg}")
                    }
                }
                onClick { it.stopPropagation() }
            },
        ) {
            if (showHeader) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .gap(Spacing.sm)
                        .styleModifier {
                            property("justify-content", "space-between")
                            property("margin-bottom", "${Spacing.md}")
                        },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    if (title != null) {
                        SpanText(
                            text = title,
                            modifier = Modifier
                                .fontSize(Typography.h2.size)
                                .fontWeight(Typography.h2.weight)
                                .color(Palette.textPrimary)
                                .attrsModifier { attr("id", titleId) },
                        )
                    } else {
                        Box(modifier = Modifier)
                    }
                    if (showClose && !submitting) {
                        IconButton(
                            ariaLabel = "Close dialog",
                            onClick = { dismissIfAllowed() },
                        ) {
                            LucideX(size = 18)
                        }
                    }
                }
            }

            content()

            if (footer != null) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .gap(Spacing.sm)
                        .padding(top = Spacing.lg)
                        .styleModifier {
                            property("justify-content", "flex-end")
                        },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    footer()
                }
            }
        }
    }
}

private fun focusFirst(dialog: HTMLElement) {
    val candidates = dialog
        .querySelectorAll(FOCUSABLE_SELECTOR)
        .asList()
        .filterIsInstance<HTMLElement>()
        .filterNot { it.getAttribute("disabled") != null }
    val first = candidates.firstOrNull()
    if (first != null) first.focus() else dialog.focus()
}

private fun trapTab(dialog: HTMLElement, e: KeyboardEvent) {
    val focusable = dialog
        .querySelectorAll(FOCUSABLE_SELECTOR)
        .asList()
        .filterIsInstance<HTMLElement>()
        .filterNot { it.getAttribute("disabled") != null }
    if (focusable.isEmpty()) {
        e.preventDefault()
        dialog.focus()
        return
    }
    val firstEl = focusable.first()
    val lastEl = focusable.last()
    val active = document.activeElement
    if (e.shiftKey) {
        if (active === firstEl || active === dialog) {
            e.preventDefault()
            lastEl.focus()
        }
    } else {
        if (active === lastEl) {
            e.preventDefault()
            firstEl.focus()
        }
    }
}

private const val FOCUSABLE_SELECTOR =
    "a[href],button,input,select,textarea,[tabindex]:not([tabindex='-1'])"
